import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import RestoreFromTrashRoundedIcon from '@mui/icons-material/RestoreFromTrashRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import { useTranslation } from 'react-i18next';
import { generatePastelColor, generateSaturatedColor } from '../Utils/colorUtils';
import { getVisionOrientationByPage } from './Viewer/TagHighlightUtils';
import { getPdfPageRotationState, getRotatedPdfThumbnailScale } from './Viewer/pdfViewerRotation';

export const getAlphabeticDocumentLabel = (index) => {
  let value = Number(index) + 1;
  if (!Number.isInteger(value) || value < 1) return 'A';

  let label = '';
  while (value > 0) {
    value -= 1;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }
  return label;
};

const ensureUniqueDocumentNames = (documents) => {
  const usedNames = new Set();
  return documents.map((document) => {
    const baseName = String(document.type || 'document').trim() || 'document';
    let uniqueName = baseName;
    if (usedNames.has(uniqueName.toLowerCase())) {
      if (/^[A-Z]+$/.test(baseName)) {
        let labelIndex = 0;
        do {
          uniqueName = getAlphabeticDocumentLabel(labelIndex);
          labelIndex += 1;
        } while (usedNames.has(uniqueName.toLowerCase()));
      } else {
        let suffix = 2;
        while (usedNames.has(uniqueName.toLowerCase())) {
          uniqueName = `${baseName}_${suffix}`;
          suffix += 1;
        }
      }
    }
    usedNames.add(uniqueName.toLowerCase());
    return { ...document, type: uniqueName };
  });
};

const getDocumentColorSeed = (name, documentIndex) => (
  `${name}::split-document-${documentIndex + 1}`
);

const PAGE_VIEW_CONFIG = {
  compact: {
    containerColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
    cardColumns: '40px minmax(0, 1fr)',
    thumbnailWidth: 40,
    thumbnailHeight: 54,
    documentFontSize: '0.72rem',
    documentChipHeight: 21,
  },
  list: {
    containerColumns: '1fr',
    cardColumns: '56px minmax(0, 1fr)',
    thumbnailWidth: 56,
    thumbnailHeight: 76,
    documentFontSize: '0.8rem',
    documentChipHeight: 23,
  },
  thumbnails: {
    containerColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    cardColumns: '84px minmax(0, 1fr)',
    thumbnailWidth: 84,
    thumbnailHeight: 112,
    documentFontSize: '0.82rem',
    documentChipHeight: 24,
  },
};

const getDragDropIntent = (event, bounds, pageView) => {
  const isHorizontalView = pageView !== 'list';
  const pointer = isHorizontalView ? event.clientX : event.clientY;
  const start = isHorizontalView ? bounds.left : bounds.top;
  const size = isHorizontalView ? bounds.width : bounds.height;
  if (!Number.isFinite(pointer) || !Number.isFinite(start) || !Number.isFinite(size) || size <= 0) {
    return { isInsertion: false, insertAfter: false };
  }
  const position = Math.max(0, Math.min(size, pointer - start));
  const insertAfter = position > size / 2;
  const edgeDistance = Math.min(position, size - position);
  const edgeThreshold = Math.min(28, size * 0.24);
  return { isInsertion: edgeDistance <= edgeThreshold, insertAfter };
};

const INITIAL_THUMBNAIL_PAGE_LIMIT = 20;

export const getInitialThumbnailPages = (pageCount) => new Set(
  Array.from(
    { length: Math.min(Math.max(Number(pageCount) || 0, 0), INITIAL_THUMBNAIL_PAGE_LIMIT) },
    (_, index) => index + 1,
  ),
);

const getPageNumber = (page) => {
  if (page && typeof page === 'object') {
    return Number(page.sourcePage ?? page.page ?? page.number);
  }
  return Number(page);
};

const getDocumentPageNumbers = (document) => (
  (Array.isArray(document?.pages) ? document.pages : [])
    .map(getPageNumber)
    .filter((page) => Number.isInteger(page) && page >= 1)
);

const getDocumentPageSequence = (documents, activeDocumentId = null, pageOrder = []) => {
  if (pageOrder.length) {
    const entries = new Map(documents.flatMap((document) => document.pages.map((page) => [page.id, { ...page, documentId: document.id }])));
    const orderedIds = [...new Set([...pageOrder, ...entries.keys()])];
    return orderedIds.map((id) => entries.get(id)).filter((page) => page && (activeDocumentId == null || page.documentId === activeDocumentId)).map(getPageNumber);
  }
  const sourceDocuments = Array.isArray(documents) ? documents : [];
  if (activeDocumentId == null) {
    return sourceDocuments.flatMap(getDocumentPageNumbers);
  }
  const activeDocument = sourceDocuments.find((document) => document.id === activeDocumentId);
  return activeDocument ? getDocumentPageNumbers(activeDocument) : sourceDocuments.flatMap(getDocumentPageNumbers);
};

const getPageInstanceId = (page, documentIndex, pageIndex, sourcePage) => {
  const persistedId = page && typeof page === 'object' ? String(page.id || '').trim() : '';
  return persistedId || `page-${documentIndex}-${pageIndex}-${sourcePage}`;
};

const normalizePageEntries = (pages, documentIndex, pageCount) => {
  const firstInstanceByPage = new Map();
  return (Array.isArray(pages) ? pages : [])
    .map((rawPage, pageIndex) => {
      const sourcePage = getPageNumber(rawPage);
      if (!Number.isInteger(sourcePage) || sourcePage < 1 || (pageCount && sourcePage > pageCount)) {
        return null;
      }
      const entry = {
        id: getPageInstanceId(rawPage, documentIndex, pageIndex, sourcePage),
        sourcePage,
        duplicatedFrom: rawPage && typeof rawPage === 'object'
          ? String(rawPage.duplicatedFrom || rawPage.duplicated_from || '').trim() || null
          : null,
      };
      if (!entry.duplicatedFrom && firstInstanceByPage.has(sourcePage)) {
        entry.duplicatedFrom = firstInstanceByPage.get(sourcePage);
      }
      if (!firstInstanceByPage.has(sourcePage)) {
        firstInstanceByPage.set(sourcePage, entry.id);
      }
      return entry;
    })
    .filter(Boolean);
};

const serializeDocuments = (documents) => documents.map((document) => ({
  pagenbs: String(getPageNumber(document.pages?.[0]) || Number(document.pagenbs) || 1),
  pages: getDocumentPageNumbers(document),
  type: String(document.type || 'document').trim() || 'document',
}));

const normalizeExcludedPages = (review, pageCount) => Array.from(new Set(
  (Array.isArray(review?.excluded_pages) ? review.excluded_pages : [])
    .map((page) => Number(page))
    .filter((page) => Number.isInteger(page) && page >= 1 && (!pageCount || page <= pageCount)),
)).sort((left, right) => left - right);

const normalizeExcludedPageOrigins = (review, excludedPages) => Object.fromEntries(
  excludedPages.flatMap((page) => {
    const origin = review?.excluded_page_origins?.[page];
    if (!origin || typeof origin !== 'object') return [];
    const documentIndex = Number(origin.documentIndex);
    const documentId = String(origin.documentId || '').trim();
    const documentType = String(origin.documentType || '').trim();
    if (!Number.isInteger(documentIndex) || documentIndex < 0 || !documentId || !documentType) return [];
    return [[page, { documentIndex, documentId, documentType }]];
  }),
);

const normalizeDocumentOrder = (review, documents, excludedPageOrigins) => {
  const activeIds = documents.map((document) => document.id);
  const activeIdSet = new Set(activeIds);
  const removedOrigins = Array.from(new Map(
    Object.values(excludedPageOrigins).map((origin) => [origin.documentId, origin]),
  ).values()).filter((origin) => !activeIdSet.has(origin.documentId));
  const validIds = new Set([...activeIds, ...removedOrigins.map((origin) => origin.documentId)]);
  const savedOrder = Array.isArray(review?.document_order) ? review.document_order : [];
  if (savedOrder.length) {
    return Array.from(new Set([
      ...savedOrder.filter((id) => validIds.has(id)),
      ...activeIds,
      ...removedOrigins.map((origin) => origin.documentId),
    ]));
  }

  const order = [...activeIds];
  removedOrigins.sort((left, right) => left.documentIndex - right.documentIndex)
    .forEach((origin) => order.splice(Math.min(origin.documentIndex, order.length), 0, origin.documentId));
  return order;
};

const normalizeDocuments = (review) => {
  const rawDocuments = Array.isArray(review?.documents) ? review.documents : [];
  const pageCount = Math.max(Number(review?.page_count) || 0, 0);
  const isExtendedReview = Number(review?.version) >= 4;
  const sortedDocuments = rawDocuments
    .map((document, index) => ({ document, originalIndex: index, startPage: Number(document?.pagenbs) }))
    .sort((left, right) => (
      isExtendedReview
        ? left.originalIndex - right.originalIndex
        : left.startPage - right.startPage || left.originalIndex - right.originalIndex
    ));

  const documents = sortedDocuments.map(({ document, originalIndex }, documentIndex) => {
    const explicitPages = Array.isArray(document?.pages) ? document.pages : null;
    const startPage = Number(document?.pagenbs);
    const nextStartPage = Number(sortedDocuments[documentIndex + 1]?.document?.pagenbs);
    const rangeStart = Number.isInteger(startPage) && startPage >= 1 ? startPage : 1;
    const rangeEnd = Number.isInteger(nextStartPage) && nextStartPage > rangeStart
      ? nextStartPage - 1
      : pageCount || rangeStart;
    const rawPages = explicitPages || Array.from(
      { length: Math.max(rangeEnd - rangeStart + 1, 1) },
      (_, pageIndex) => rangeStart + pageIndex,
    );
    const pages = normalizePageEntries(
      isExtendedReview ? rawPages : Array.from(new Set(rawPages)).sort((left, right) => left - right),
      documentIndex,
      pageCount,
    );

    const fallbackName = getAlphabeticDocumentLabel(documentIndex);
    const documentName = String(document?.name || document?.type || fallbackName).trim() || fallbackName;
    return {
      id: String(document?.id || review?.document_ids?.[originalIndex] || '').trim() || `document-${documentIndex}`,
      pagenbs: String(getPageNumber(pages[0]) || rangeStart),
      pages,
      type: documentName,
    };
  }).filter((document) => document.pages.length > 0);

  if (documents.length === 0) {
    if (isExtendedReview) return [];
    return [{
      id: 'document-0',
      pagenbs: '1',
      pages: pageCount ? [{ id: 'page-0-0-1', sourcePage: 1 }] : [],
      type: 'A',
    }];
  }
  return ensureUniqueDocumentNames(documents);
};

const getPageCount = (review, thumbnailPageCount) => Math.max(
  Number(review?.page_count) || 0,
  Number(thumbnailPageCount) || 0,
);

const formatPageRanges = (pages) => {
  const pageNumbers = (Array.isArray(pages) ? pages : [])
    .map(getPageNumber)
    .filter((page) => Number.isInteger(page) && page >= 1);
  const hasDuplicates = new Set(pageNumbers).size !== pageNumbers.length;
  const isOrdered = pageNumbers.every((page, index) => index === 0 || page >= pageNumbers[index - 1]);
  if (hasDuplicates || !isOrdered) return pageNumbers.join(', ');

  const sortedPages = Array.from(new Set(pageNumbers)).sort((left, right) => left - right);
  if (!sortedPages.length) return '';

  const ranges = [];
  let rangeStart = sortedPages[0];
  let rangeEnd = rangeStart;
  sortedPages.slice(1).forEach((page) => {
    if (page === rangeEnd + 1) {
      rangeEnd = page;
      return;
    }
    ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}–${rangeEnd}`);
    rangeStart = page;
    rangeEnd = page;
  });
  ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}–${rangeEnd}`);
  return ranges.join(', ');
};

const SplitReviewEditor = ({
  review,
  timeevent = null,
  pageRotations = null,
  pdfUrl,
  focusPage = null,
  focusPageIndex = null,
  focusPageRequest = 0,
  onDocumentFilterChange = null,
  disabled = false,
  saving = false,
  onChange,
  onPageSelect,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const reviewKey = JSON.stringify({
    version: review?.version || 0,
    page_count: review?.page_count || 0,
    documents: review?.documents || [],
    excluded_pages: review?.excluded_pages || [],
    excluded_page_origins: review?.excluded_page_origins || {},
    document_ids: review?.document_ids || [],
    document_order: review?.document_order || [],
    page_order: review?.page_order || [],
  });
  const initialDocuments = useMemo(() => normalizeDocuments(JSON.parse(reviewKey)), [reviewKey]);
  const initialExcludedPages = useMemo(
    () => normalizeExcludedPages(JSON.parse(reviewKey), getPageCount(JSON.parse(reviewKey), 0)),
    [reviewKey],
  );
  const initialExcludedPageOrigins = useMemo(
    () => normalizeExcludedPageOrigins(JSON.parse(reviewKey), initialExcludedPages),
    [initialExcludedPages, reviewKey],
  );
  const initialDocumentOrder = useMemo(
    () => normalizeDocumentOrder(JSON.parse(reviewKey), initialDocuments, initialExcludedPageOrigins),
    [initialDocuments, initialExcludedPageOrigins, reviewKey],
  );
  const [documents, setDocuments] = useState(initialDocuments);
  const [documentOrder, setDocumentOrder] = useState(initialDocumentOrder);
  const [pageOrder, setPageOrder] = useState([]);
  const [excludedPages, setExcludedPages] = useState(initialExcludedPages);
  const [excludedPageOrigins, setExcludedPageOrigins] = useState(initialExcludedPageOrigins);
  const [thumbnailPages, setThumbnailPages] = useState([]);
  const [thumbnailPageCount, setThumbnailPageCount] = useState(0);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const [thumbnailError, setThumbnailError] = useState('');
  const [thumbnailPdfVersion, setThumbnailPdfVersion] = useState(0);
  const thumbnailContainerRef = useRef(null);
  const thumbnailCardRefs = useRef(new Map());
  const thumbnailPdfRef = useRef(null);
  const renderedThumbnailPagesRef = useRef(new Set());
  const renderingThumbnailPagesRef = useRef(new Set());
  const visibleThumbnailPagesRef = useRef(new Set());
  const [visibleThumbnailPages, setVisibleThumbnailPages] = useState(new Set());
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const [draggedPages, setDraggedPages] = useState([]);
  const dragSourcePageRef = useRef(null);
  const [dragPreviewTargetPage, setDragPreviewTargetPage] = useState(null);
  const [dragInsertionTarget, setDragInsertionTarget] = useState(null);
  const hoverPageTimerRef = useRef(null);
  const [hoveredPage, setHoveredPage] = useState(null);
  const [hoverNavigationTarget, setHoverNavigationTarget] = useState(null);
  const [activeDocumentId, setActiveDocumentId] = useState(null);
  const [pageMenu, setPageMenu] = useState({ anchorEl: null, pageId: null });
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const [actionNotice, setActionNotice] = useState('');
  const pageInstanceSequenceRef = useRef(0);
  const [pageView, setPageView] = useState('compact');
  const visionOrientationByPage = useMemo(
    () => getVisionOrientationByPage(timeevent),
    [timeevent],
  );
  const pageCount = getPageCount(review, thumbnailPageCount);
  const activeDocumentIndex = useMemo(() => {
    if (activeDocumentId == null) return -1;
    return documents.findIndex((document) => document.id === activeDocumentId);
  }, [activeDocumentId, documents]);
  const pageEntries = useMemo(() => documents.flatMap((document, documentIndex) => (
    (Array.isArray(document.pages) ? document.pages : []).map((page, pageIndex) => ({
      ...page,
      documentId: document.id,
      documentIndex,
      pageIndex,
    }))
  )), [documents]);
  const pageEntryById = useMemo(
    () => new Map(pageEntries.map((page) => [page.id, page])),
    [pageEntries],
  );
  const visiblePageEntries = useMemo(() => {
    const ranks = new Map(pageOrder.map((id, index) => [id, index]));
    const ordered = [...pageEntries].sort((a, b) => (ranks.get(a.id) ?? Infinity) - (ranks.get(b.id) ?? Infinity));
    if (activeDocumentIndex >= 0) {
      return ordered.filter((page) => page.documentIndex === activeDocumentIndex);
    }
    return ordered;
  }, [activeDocumentIndex, pageEntries, pageOrder]);
  const visiblePageNumbers = useMemo(
    () => visiblePageEntries.map((page) => page.sourcePage),
    [visiblePageEntries],
  );
  const assignedSourcePages = useMemo(
    () => new Set(pageEntries.map((page) => page.sourcePage)),
    [pageEntries],
  );
  const unassignedPages = useMemo(() => Array.from(
    { length: pageCount },
    (_, index) => index + 1,
  ).filter((page) => !assignedSourcePages.has(page) && !excludedPages.includes(page)), [assignedSourcePages, excludedPages, pageCount]);
  const hasEmptyDocument = documents.some((document) => !document.pages.length);
  const canApprove = documents.length >= 2 && !hasEmptyDocument && unassignedPages.length === 0;

  const cancelPageHover = useCallback(() => {
    if (hoverPageTimerRef.current) {
      clearTimeout(hoverPageTimerRef.current);
      hoverPageTimerRef.current = null;
    }
  }, []);

  const schedulePageHover = useCallback((pageEntry) => {
    if (disabled || !onPageSelect) return;
    const pageNumber = Number(pageEntry?.sourcePage);
    const pageIndex = visiblePageEntries.findIndex((page) => page.id === pageEntry?.id);
    if (!Number.isInteger(pageNumber) || pageIndex < 0) return;
    cancelPageHover();
    setHoverNavigationTarget({ id: pageEntry.id, pageNumber, pageIndex });
    hoverPageTimerRef.current = setTimeout(() => {
      onPageSelect({ page: pageNumber, pageIndex });
      hoverPageTimerRef.current = null;
    }, 500);
  }, [cancelPageHover, disabled, onPageSelect, visiblePageEntries]);

  useEffect(() => cancelPageHover, [cancelPageHover]);

  useEffect(() => {
    if (
      hoverNavigationTarget
      && Number(focusPage) === hoverNavigationTarget.pageNumber
      && focusPageIndex != null
      && Number(focusPageIndex) === hoverNavigationTarget.pageIndex
    ) {
      setHoverNavigationTarget(null);
    }
  }, [focusPage, focusPageIndex, focusPageRequest, hoverNavigationTarget]);

  useEffect(() => {
    setDocuments(initialDocuments);
    setDocumentOrder(initialDocumentOrder);
    const savedReview = JSON.parse(reviewKey);
    const documentsById = new Map(initialDocuments.map((document) => [document.id, document]));
    const restoredOrder = (savedReview.page_order || []).map(({ documentIndex, pageIndex }) => {
      const documentId = String(
        savedReview.documents?.[documentIndex]?.id
        || savedReview.document_ids?.[documentIndex]
        || `document-${documentIndex}`,
      ).trim();
      return documentsById.get(documentId)?.pages[pageIndex]?.id;
    }).filter(Boolean);
    setPageOrder(restoredOrder);
    setExcludedPages(initialExcludedPages);
    setExcludedPageOrigins(initialExcludedPageOrigins);
    setSelectedPages([]);
    setSelectionAnchor(null);
    setActiveDocumentId(null);
    setPageMenu({ anchorEl: null, pageId: null });
    setUndoSnapshot(null);
    onDocumentFilterChange?.(getDocumentPageSequence(initialDocuments, null, restoredOrder));
  }, [initialDocuments, initialDocumentOrder, initialExcludedPages, initialExcludedPageOrigins, onDocumentFilterChange, reviewKey]);

  const renderThumbnail = useCallback(async (pageNumber) => {
    const pdf = thumbnailPdfRef.current;
    if (
      !pdf
      || renderedThumbnailPagesRef.current.has(pageNumber)
      || renderingThumbnailPagesRef.current.has(pageNumber)
    ) {
      return;
    }

    renderingThumbnailPagesRef.current.add(pageNumber);
    try {
      const page = await pdf.getPage(pageNumber);
      try {
        const visionOrientation = visionOrientationByPage[pageNumber] || { rotation: 0, fineAngle: 0 };
        const pageRotation = getPdfPageRotationState({
          pdfPageRotation: page.rotate,
          visionRotation: visionOrientation.rotation,
          correctionRotation: visionOrientation.correctionRotation,
          metadataPdfRotation: visionOrientation.pdfRotation,
          finalRotation: visionOrientation.finalRotation,
          rotationBakedIntoPdf: visionOrientation.rotationBakedIntoPdf,
        }).renderRotation;
        const viewport = page.getViewport({ scale: 0.18, rotation: pageRotation });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Unable to render page thumbnail');

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
        const dataUrl = canvas.toDataURL('image/jpeg', 0.78);
        if (!visibleThumbnailPagesRef.current.has(pageNumber)) return;
        renderedThumbnailPagesRef.current.add(pageNumber);
        setThumbnailPages((previousPages) => (
          previousPages.some((thumbnail) => thumbnail.pageNumber === pageNumber)
            ? previousPages
            : [...previousPages, { pageNumber, dataUrl }]
        ));
      } finally {
        page.cleanup();
      }
    } catch (error) {
      console.error(`Error rendering split review thumbnail for page ${pageNumber}:`, error);
    } finally {
      renderingThumbnailPagesRef.current.delete(pageNumber);
    }
  }, [visionOrientationByPage]);

  useEffect(() => {
    if (!pdfUrl) {
      thumbnailPdfRef.current = null;
      setThumbnailPages([]);
      setThumbnailPageCount(0);
      setVisibleThumbnailPages(new Set());
      setThumbnailLoading(false);
      setThumbnailError('');
      return undefined;
    }

    let cancelled = false;
    let loadingTask = null;
    let loadedPdf = null;
    const previousPdf = thumbnailPdfRef.current;
    thumbnailPdfRef.current = null;
    if (previousPdf) previousPdf.destroy();
    renderedThumbnailPagesRef.current.clear();
    renderingThumbnailPagesRef.current.clear();
    setThumbnailPages([]);
    setThumbnailPageCount(0);
    setVisibleThumbnailPages(new Set());
    setThumbnailLoading(true);
    setThumbnailError('');

    const loadPdf = async () => {
      try {
        const { GlobalWorkerOptions, getDocument } = await import('pdfjs-dist/build/pdf.mjs');
        if (cancelled) return;

        GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
        loadingTask = getDocument({ url: pdfUrl, isEvalSupported: false });
        const pdf = await loadingTask.promise;
        if (cancelled) {
          await pdf.destroy();
          return;
        }

        loadedPdf = pdf;
        thumbnailPdfRef.current = pdf;
        setThumbnailPageCount(pdf.numPages);
        setThumbnailPdfVersion((previousVersion) => previousVersion + 1);
        setThumbnailLoading(false);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading split review PDF thumbnails:', error);
          setThumbnailError(t('validation_workspace.split_review_thumbnail_error'));
          setThumbnailLoading(false);
        }
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
      thumbnailPdfRef.current = null;
      if (loadingTask) loadingTask.destroy();
      if (loadedPdf) loadedPdf.destroy();
    };
  }, [pdfUrl, t, visionOrientationByPage]);

  useEffect(() => {
    visibleThumbnailPagesRef.current = visibleThumbnailPages;
    renderedThumbnailPagesRef.current.forEach((pageNumber) => {
      if (!visibleThumbnailPages.has(pageNumber)) {
        renderedThumbnailPagesRef.current.delete(pageNumber);
      }
    });
    setThumbnailPages((previousPages) => {
      const nextPages = previousPages.filter((thumbnail) => visibleThumbnailPages.has(thumbnail.pageNumber));
      return nextPages.length === previousPages.length ? previousPages : nextPages;
    });
  }, [visibleThumbnailPages]);

  useEffect(() => {
    visibleThumbnailPages.forEach((pageNumber) => {
      renderThumbnail(pageNumber);
    });
  }, [renderThumbnail, thumbnailPdfVersion, visibleThumbnailPages]);

  const setThumbnailCardRef = useCallback((pageId, node) => {
    if (!node) {
      thumbnailCardRefs.current.delete(pageId);
      return;
    }
    if (pageId) {
      thumbnailCardRefs.current.set(pageId, node);
    }
  }, []);

  useEffect(() => {
    const root = thumbnailContainerRef.current;
    if (!root || !pageCount) return undefined;

    const thumbnailSeedPages = Array.from(new Set([
      ...visiblePageNumbers,
      ...excludedPages,
    ])).slice(0, INITIAL_THUMBNAIL_PAGE_LIMIT);

    if (typeof IntersectionObserver === 'undefined') {
      setVisibleThumbnailPages(new Set(thumbnailSeedPages));
      return undefined;
    }

    // Some browsers do not emit the initial observer entries until the scroll
    // container is laid out again (for example after changing the view mode).
    // Seed the first viewport so thumbnails begin rendering immediately.
    setVisibleThumbnailPages((previousPages) => (
      previousPages.size > 0
        ? previousPages
        : new Set(thumbnailSeedPages)
    ));

    const observer = new IntersectionObserver((entries) => {
      setVisibleThumbnailPages((previousPages) => {
        const nextPages = new Set(previousPages);
        let changed = false;
        entries.forEach((entry) => {
          const pageNumber = Number(entry.target.dataset.pageNumber);
          if (!Number.isInteger(pageNumber)) return;
          if (entry.isIntersecting && !nextPages.has(pageNumber)) {
            nextPages.add(pageNumber);
            changed = true;
          } else if (!entry.isIntersecting && nextPages.delete(pageNumber)) {
            changed = true;
          }
        });
        return changed ? nextPages : previousPages;
      });
    }, { root, rootMargin: '600px 0px' });

    thumbnailCardRefs.current.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [excludedPages, pageCount, pageView, pdfUrl, visiblePageNumbers]);

  const pageViewConfig = PAGE_VIEW_CONFIG[pageView] || PAGE_VIEW_CONFIG.compact;
  const thumbnailByPage = useMemo(() => new Map(
    thumbnailPages.map((thumbnail) => [thumbnail.pageNumber, thumbnail.dataUrl])
  ), [thumbnailPages]);
  const selectedPageSet = useMemo(() => new Set(selectedPages), [selectedPages]);
  const draggedPageSet = useMemo(() => new Set(draggedPages), [draggedPages]);

  const updateDocuments = useCallback((nextDocuments, nextExcludedPages = excludedPages, nextOrder = pageOrder, nextOrigins = excludedPageOrigins) => {
    const normalizedDocuments = ensureUniqueDocumentNames(nextDocuments
      .map((document, documentIndex) => {
        const pages = normalizePageEntries(document.pages, documentIndex, pageCount);
        return {
          ...document,
          id: String(document.id || '').trim() || `document-${documentIndex}`,
          pagenbs: String(getPageNumber(pages[0]) || Number(document.pagenbs) || 1),
          pages,
          type: String(document.type || 'document').trim() || 'document',
        };
      })
      .filter((document) => document.pages.length > 0));
    const normalizedExcludedPages = Array.from(new Set(nextExcludedPages
      .map((page) => Number(page))
      .filter((page) => Number.isInteger(page) && page >= 1 && page <= pageCount)))
      .sort((left, right) => left - right);
    const normalizedOrigins = normalizeExcludedPageOrigins(
      { excluded_page_origins: nextOrigins }, normalizedExcludedPages,
    );
    const retainedIds = new Set([
      ...normalizedDocuments.map((document) => document.id),
      ...Object.values(normalizedOrigins).map((origin) => origin.documentId),
    ]);
    const nextDocumentOrder = Array.from(new Set([
      ...documentOrder.filter((id) => retainedIds.has(id)),
      ...normalizedDocuments.map((document) => document.id),
    ]));
    setDocuments(normalizedDocuments);
    setDocumentOrder(nextDocumentOrder);
    setExcludedPages(normalizedExcludedPages);
    setExcludedPageOrigins(normalizedOrigins);
    setPageOrder(nextOrder);
    const nextActiveDocumentId = normalizedDocuments.some((document) => document.id === activeDocumentId)
      ? activeDocumentId : null;
    if (nextActiveDocumentId !== activeDocumentId) setActiveDocumentId(null);
    const positions = new Map(normalizedDocuments.flatMap((document, documentIndex) => document.pages.map((page, pageIndex) => [page.id, { documentIndex, pageIndex }])));
    onDocumentFilterChange?.(getDocumentPageSequence(normalizedDocuments, nextActiveDocumentId, nextOrder));
    onChange?.({
      version: 4,
      documents: serializeDocuments(normalizedDocuments),
      excluded_pages: normalizedExcludedPages,
      ...(Object.keys(normalizedOrigins).length ? {
        excluded_page_origins: normalizedOrigins,
        document_ids: normalizedDocuments.map((document) => document.id),
        document_order: nextDocumentOrder,
      } : {}),
      ...(nextOrder.length ? { page_order: [...new Set([...nextOrder, ...positions.keys()])].map((id) => positions.get(id)).filter(Boolean) } : {}),
    });
  }, [activeDocumentId, documentOrder, excludedPageOrigins, excludedPages, onChange, onDocumentFilterChange, pageCount, pageOrder]);

  const togglePageSelection = useCallback((pageEntry, event = {}) => {
    if (disabled) return;
    const pageId = pageEntry?.id;
    const pageNumber = Number(pageEntry?.sourcePage);
    if (!pageId || !Number.isInteger(pageNumber)) return;
    cancelPageHover();
    setHoverNavigationTarget(null);
    onPageSelect?.({
      page: pageNumber,
      pageIndex: visiblePageEntries.findIndex((page) => page.id === pageId),
    });
    const isRangeSelection = event.shiftKey && selectionAnchor != null;
    const isMultiSelection = event.ctrlKey || event.metaKey;

    if (isRangeSelection) {
      const anchorIndex = visiblePageEntries.findIndex((page) => page.id === selectionAnchor);
      const pageIndex = visiblePageEntries.findIndex((page) => page.id === pageId);
      if (anchorIndex < 0 || pageIndex < 0) return;
      const start = Math.min(anchorIndex, pageIndex);
      const end = Math.max(anchorIndex, pageIndex);
      const range = visiblePageEntries.slice(start, end + 1).map((page) => page.id);
      setSelectedPages((previousPages) => (
        isMultiSelection
          ? Array.from(new Set([...previousPages, ...range]))
          : range
      ));
    } else if (isMultiSelection) {
      setSelectedPages((previousPages) => (
        previousPages.includes(pageId)
          ? previousPages.filter((page) => page !== pageId)
          : [...previousPages, pageId]
      ));
      setSelectionAnchor(pageId);
    } else {
      setSelectedPages((previousPages) => (
        previousPages.includes(pageId)
          ? previousPages.filter((page) => page !== pageId)
          : [...previousPages, pageId]
      ));
      setSelectionAnchor(pageId);
    }
  }, [cancelPageHover, disabled, onPageSelect, selectionAnchor, visiblePageEntries]);

  const getPageEntriesById = useCallback((pageIds) => (
    pageIds.map((pageId) => pageEntryById.get(pageId)).filter(Boolean)
  ), [pageEntryById]);

  const createDocumentFromSelectedPages = useCallback((pageIds) => {
    if (disabled || !pageIds.length) return;
    const pagesToGroup = getPageEntriesById(pageIds);
    if (!pagesToGroup.length) return;

    const selectedPageSetForGrouping = new Set(pagesToGroup.map((page) => page.id));
    const sourceDocument = documents[pagesToGroup[0].documentIndex];
    const remainingDocuments = documents
      .map((document) => ({
        ...document,
        pages: document.pages.filter((page) => !selectedPageSetForGrouping.has(page.id)),
      }))
      .filter((document) => document.pages.length > 0);
    updateDocuments([
      ...remainingDocuments,
      {
        id: `document-${Date.now()}`,
        pagenbs: String(pagesToGroup[0].sourcePage),
        pages: pagesToGroup,
        type: sourceDocument?.type || 'document',
      },
    ]);
    setUndoSnapshot(null);
    setSelectedPages([]);
    setSelectionAnchor(null);
  }, [disabled, documents, getPageEntriesById, updateDocuments]);

  const movePageInstancesToDocument = useCallback((pageIds, targetDocumentId, targetPageId = null, insertAfter = false) => {
    if (disabled || !pageIds.length || !targetDocumentId) return;
    const pagesToMove = getPageEntriesById(pageIds);
    const targetDocumentIndex = documents.findIndex((document) => document.id === targetDocumentId);
    if (!pagesToMove.length || targetDocumentIndex < 0) return;
    if (pagesToMove.every((page) => page.documentId === targetDocumentId)) return;

    const pagesToMoveSet = new Set(pagesToMove.map((page) => page.id));
    const nextDocuments = documents.map((document, documentIndex) => {
      const remainingPages = document.pages.filter((page) => !pagesToMoveSet.has(page.id));
      if (documentIndex !== targetDocumentIndex) {
        return { ...document, pages: remainingPages };
      }

      const targetIndex = targetPageId
        ? remainingPages.findIndex((page) => page.id === targetPageId)
        : -1;
      if (targetIndex >= 0) {
        remainingPages.splice(targetIndex + (insertAfter ? 1 : 0), 0, ...pagesToMove);
      } else {
        remainingPages.push(...pagesToMove);
      }
      return {
        ...document,
        pages: remainingPages,
      };
    });

    updateDocuments(nextDocuments);
    setUndoSnapshot(null);
    setSelectedPages([]);
    setSelectionAnchor(null);
  }, [disabled, documents, getPageEntriesById, updateDocuments]);

  const reorderPageInstances = useCallback((pageIds, targetPageId, insertAfter = false) => {
    if (disabled || !pageIds.length) return;
    const pagesToMove = getPageEntriesById(pageIds);
    const targetPage = pageEntryById.get(targetPageId);
    if (!pagesToMove.length || !targetPage) return;
    const pagesToMoveSet = new Set(pagesToMove.map((page) => page.id));
    if (pagesToMoveSet.has(targetPageId)) return;
    const ranks = new Map(pageOrder.map((id, index) => [id, index]));
    const orderedPages = [...pageEntries].sort((a, b) => (ranks.get(a.id) ?? Infinity) - (ranks.get(b.id) ?? Infinity));
    const movingPages = orderedPages.filter((page) => pagesToMoveSet.has(page.id));
    const remainingPages = orderedPages.filter((page) => !pagesToMoveSet.has(page.id));
    const targetIndex = remainingPages.findIndex((page) => page.id === targetPageId);
    if (targetIndex < 0) return;
    remainingPages.splice(targetIndex + (insertAfter ? 1 : 0), 0, ...movingPages);
    const grouped = new Map(documents.map((document) => [document.id, []]));
    remainingPages.forEach((page) => grouped.get(page.documentId).push(page));
    updateDocuments(documents.map((document) => ({ ...document, pages: grouped.get(document.id) })), excludedPages, remainingPages.map((page) => page.id));
    setUndoSnapshot(null);
    setSelectedPages([]);
    setSelectionAnchor(null);
  }, [disabled, documents, excludedPages, getPageEntriesById, pageEntries, pageOrder, pageEntryById, updateDocuments]);

  const duplicatePage = useCallback((pageId) => {
    if (disabled) return;
    const sourcePage = pageEntryById.get(pageId);
    if (!sourcePage) return;
    const duplicate = {
      id: `${sourcePage.id}-copy-${pageInstanceSequenceRef.current++}`,
      sourcePage: sourcePage.sourcePage,
      duplicatedFrom: sourcePage.duplicatedFrom || sourcePage.id,
    };
    updateDocuments(documents.map((document) => {
      if (document.id !== sourcePage.documentId) return document;
      const pages = [...document.pages];
      pages.splice(sourcePage.pageIndex + 1, 0, duplicate);
      return { ...document, pages };
    }));
    setUndoSnapshot(null);
    setSelectedPages([duplicate.id]);
    setSelectionAnchor(duplicate.id);
    setPageMenu({ anchorEl: null, pageId: null });
    setActionNotice(t('validation_workspace.split_review_page_duplicated'));
  }, [disabled, documents, pageEntryById, t, updateDocuments]);

  const removePage = useCallback((pageIds) => {
    if (disabled || !pageIds.length) return;
    const pagesToRemove = getPageEntriesById(pageIds);
    if (!pagesToRemove.length) return;
    const removedSet = new Set(pagesToRemove.map((page) => page.id));
    const nextDocuments = documents.map((document) => ({
      ...document,
      pages: document.pages.filter((page) => !removedSet.has(page.id)),
    }));
    const remainingSourcePages = new Set(nextDocuments.flatMap(getDocumentPageNumbers));
    const nextExcludedPages = Array.from(new Set([
      ...excludedPages,
      ...pagesToRemove.map((page) => page.sourcePage),
    ])).filter((page) => !remainingSourcePages.has(page));
    const nextOrigins = { ...excludedPageOrigins };
    const documentRanks = new Map(documentOrder.map((documentId, index) => [documentId, index]));
    pagesToRemove.forEach((page) => {
      if (!nextExcludedPages.includes(page.sourcePage) || nextOrigins[page.sourcePage]) return;
      const document = documents[page.documentIndex];
      nextOrigins[page.sourcePage] = {
        documentId: document.id,
        documentIndex: documentRanks.get(document.id) ?? page.documentIndex,
        documentType: document.type,
      };
    });
    setUndoSnapshot({ documents, excludedPages });
    updateDocuments(nextDocuments, nextExcludedPages, pageOrder, nextOrigins);
    setSelectedPages((previousPages) => previousPages.filter((pageId) => !removedSet.has(pageId)));
    setSelectionAnchor(null);
    setPageMenu({ anchorEl: null, pageId: null });
    setActionNotice(t('validation_workspace.split_review_page_removed'));
  }, [disabled, documentOrder, documents, excludedPageOrigins, excludedPages, getPageEntriesById, pageOrder, t, updateDocuments]);

  const restorePage = useCallback((sourcePage) => {
    if (disabled) return;
    const origin = excludedPageOrigins[sourcePage];
    const targetDocument = documents.find((document) => document.id === origin?.documentId) || documents[0];
    const restoredPage = {
      id: `page-restored-${sourcePage}-${pageInstanceSequenceRef.current++}`,
      sourcePage,
    };
    const documentRanks = new Map(documentOrder.map((documentId, index) => [documentId, index]));
    const originRank = documentRanks.get(origin?.documentId);
    const insertionIndex = originRank == null
      ? Math.min(origin?.documentIndex ?? documents.length, documents.length)
      : documents.filter((document) => (documentRanks.get(document.id) ?? Infinity) < originRank).length;
    const nextDocuments = origin && !documents.some((document) => document.id === origin.documentId)
      ? [...documents.slice(0, insertionIndex), {
        id: origin.documentId,
        pagenbs: String(sourcePage),
        pages: [restoredPage],
        type: origin.documentType,
      }, ...documents.slice(insertionIndex)]
      : documents.map((document) => (
        document.id === targetDocument?.id
          ? { ...document, pages: [...document.pages, restoredPage] }
          : document
      ));
    updateDocuments(
      targetDocument || origin ? nextDocuments : [{
        id: 'document-0', pagenbs: String(sourcePage), pages: [restoredPage], type: 'A',
      }],
      excludedPages.filter((page) => page !== sourcePage),
      pageOrder,
      Object.fromEntries(Object.entries(excludedPageOrigins).filter(([page]) => Number(page) !== sourcePage)),
    );
    setUndoSnapshot(null);
    setActionNotice(t('validation_workspace.split_review_page_restored'));
  }, [disabled, documentOrder, documents, excludedPageOrigins, excludedPages, pageOrder, t, updateDocuments]);

  const movePageToDocument = useCallback((pageId, targetDocumentId) => {
    movePageInstancesToDocument([pageId], targetDocumentId);
    setPageMenu({ anchorEl: null, pageId: null });
  }, [movePageInstancesToDocument]);

  const handlePageDragStart = useCallback((event, pageEntry) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    cancelPageHover();
    setHoverNavigationTarget(null);
    onPageSelect?.({
      page: pageEntry.sourcePage,
      pageIndex: visiblePageEntries.findIndex((page) => page.id === pageEntry.id),
    });
    const dragPages = selectedPageSet.has(pageEntry.id) ? selectedPages : [pageEntry.id];
    if (!selectedPageSet.has(pageEntry.id)) {
      setSelectedPages(dragPages);
      setSelectionAnchor(pageEntry.id);
    }
    setDraggedPages(dragPages);
    dragSourcePageRef.current = pageEntry.id;
    setDragPreviewTargetPage(null);
    setDragInsertionTarget(null);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', JSON.stringify(dragPages));
  }, [cancelPageHover, disabled, onPageSelect, selectedPageSet, selectedPages, visiblePageEntries]);

  const handlePageDragOver = useCallback((event, pageId) => {
    if (disabled || !draggedPages.length) return;
    event.preventDefault();
    event.stopPropagation();
    cancelPageHover();
    setHoverNavigationTarget(null);
    const bounds = event.currentTarget.getBoundingClientRect();
    const { isInsertion, insertAfter } = getDragDropIntent(event, bounds, pageView);
    setDragPreviewTargetPage(pageId);
    setDragInsertionTarget(isInsertion
      ? (previousTarget) => (
        previousTarget?.pageId === pageId && previousTarget.insertAfter === insertAfter
          ? previousTarget
          : { pageId, insertAfter }
      )
      : null);
  }, [cancelPageHover, disabled, draggedPages.length, pageView]);

  const clearDragPreview = useCallback(() => {
    setDraggedPages([]);
    setDragPreviewTargetPage(null);
    setDragInsertionTarget(null);
  }, []);

  const handlePageDrop = useCallback((event, pageId) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    let draggedPages = selectedPages;
    try {
      const parsedPages = JSON.parse(event.dataTransfer.getData('text/plain'));
      if (Array.isArray(parsedPages)) draggedPages = parsedPages;
    } catch (error) {
      // The selected page state is the fallback for browsers that omit drag data.
    }
    const targetPage = pageEntryById.get(pageId);
    const bounds = event.currentTarget.getBoundingClientRect();
    const { isInsertion, insertAfter } = getDragDropIntent(event, bounds, pageView);
    clearDragPreview();
    const draggedEntries = getPageEntriesById(draggedPages);
    if (!targetPage || !draggedEntries.length || draggedEntries.some((page) => page.id === pageId)) return;
    if (!isInsertion) {
      const sourcePage = pageEntryById.get(dragSourcePageRef.current) || draggedEntries[0];
      movePageInstancesToDocument([...draggedPages, pageId], sourcePage.documentId);
      return;
    }
    reorderPageInstances(draggedPages, pageId, insertAfter);
  }, [clearDragPreview, disabled, getPageEntriesById, movePageInstancesToDocument, pageEntryById, pageView, reorderPageInstances, selectedPages]);

  // Gaps belong to the closest visible card edge, including wrapped grid rows.
  const handlePageGapDrag = (event, isDrop = false) => {
    if (disabled || !draggedPages.length) return;
    let closest = null;
    visiblePageEntries.forEach((page) => {
      if (draggedPageSet.has(page.id)) return;
      const node = thumbnailCardRefs.current.get(page.id);
      if (!node) return;
      const bounds = node.getBoundingClientRect();
      const dx = Math.max(bounds.left - event.clientX, 0, event.clientX - bounds.right);
      const dy = Math.max(bounds.top - event.clientY, 0, event.clientY - bounds.bottom);
      const distance = dx * dx + dy * dy;
      if (!closest || distance < closest.distance) closest = { page, node, bounds, distance };
    });
    if (!closest) return;
    const { bounds, node, page } = closest;
    const horizontal = pageView !== 'list';
    const after = horizontal
      ? event.clientX > bounds.left + bounds.width / 2
      : event.clientY > bounds.top + bounds.height / 2;
    const edgeEvent = {
      currentTarget: node,
      clientX: horizontal ? (after ? bounds.right : bounds.left) : event.clientX,
      clientY: horizontal ? event.clientY : (after ? bounds.bottom : bounds.top),
      dataTransfer: event.dataTransfer,
      preventDefault: () => event.preventDefault(),
      stopPropagation: () => event.stopPropagation(),
    };
    if (isDrop) handlePageDrop(edgeEvent, page.id);
    else handlePageDragOver(edgeEvent, page.id);
  };

  const updateDocumentType = useCallback((documentIndex, type) => {
    updateDocuments(documents.map((document, index) => (
      index === documentIndex ? { ...document, type } : document
    )));
  }, [documents, updateDocuments]);

  const toggleDocumentFilter = useCallback((document) => {
    if (!document?.id) return;
    const nextDocumentId = activeDocumentId === document.id ? null : document.id;
    setActiveDocumentId(nextDocumentId);
    setSelectedPages([]);
    setSelectionAnchor(null);
    onDocumentFilterChange?.(
      getDocumentPageSequence(documents, nextDocumentId, pageOrder),
      {
        focusPage: nextDocumentId == null ? null : getDocumentPageNumbers(document)[0] || null,
        focusPageIndex: nextDocumentId == null ? null : 0,
      },
    );
  }, [activeDocumentId, documents, onDocumentFilterChange, pageOrder]);

  const clearDocumentFilter = useCallback(() => {
    setActiveDocumentId(null);
    setSelectedPages([]);
    setSelectionAnchor(null);
    onDocumentFilterChange?.(getDocumentPageSequence(documents, null, pageOrder));
  }, [documents, onDocumentFilterChange, pageOrder]);

  const undoLastAction = useCallback(() => {
    if (!undoSnapshot) return;
    updateDocuments(undoSnapshot.documents, undoSnapshot.excludedPages);
    setUndoSnapshot(null);
    setActionNotice('');
  }, [undoSnapshot, updateDocuments]);

  const pageMenuEntry = pageMenu.pageId ? pageEntryById.get(pageMenu.pageId) : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 1, lg: 1.5 },
        borderColor: 'rgba(245, 158, 11, 0.28)',
        borderRadius: 3,
        bgcolor: 'rgba(255, 255, 255, 0.96)',
        boxShadow: '0 14px 34px rgba(79, 70, 36, 0.08)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #f59e0b, #f97316)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, pt: 0.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 2,
            color: 'warning.dark',
            bgcolor: 'rgba(245, 158, 11, 0.14)',
          }}
        >
          <ContentCutRoundedIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
            {t('validation_workspace.split_review_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.5 }}>
            {t('validation_workspace.split_review_editor_help')}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 1.5,
          p: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75,
          border: '1px solid',
          borderColor: 'rgba(87, 87, 176, 0.12)',
          borderRadius: 2,
          bgcolor: 'rgba(87, 87, 176, 0.035)',
        }}
      >
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            mr: 'auto',
            px: 1,
            py: 0.5,
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            whiteSpace: 'nowrap',
          }}
        >
          {t('validation_workspace.split_review_selected_pages', { count: selectedPages.length })}
        </Typography>
        <Tooltip title={t('validation_workspace.split_review_select_all_tooltip')} arrow>
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={disabled || !visiblePageNumbers.length}
              onClick={() => setSelectedPages(visiblePageEntries.map((page) => page.id))}
            >
              {t('validation_workspace.split_review_select_all')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={t('validation_workspace.split_review_clear_selection_tooltip')} arrow>
          <span>
            <Button
              size="small"
              variant="text"
              disabled={disabled || !selectedPages.length}
              onClick={() => {
                setSelectedPages([]);
                setSelectionAnchor(null);
              }}
            >
              {t('validation_workspace.split_review_clear_selection')}
            </Button>
          </span>
        </Tooltip>
        {selectedPages.length > 0 && (
          <Tooltip title={t('validation_workspace.split_review_group_selected_tooltip', { count: selectedPages.length })} arrow>
            <Box component="span" sx={{ display: 'inline-flex', width: { xs: '100%', sm: 'auto' } }}>
              <Button
                size="small"
                variant="contained"
                disabled={disabled}
                onClick={() => createDocumentFromSelectedPages(selectedPages)}
                sx={{ width: { xs: '100%', sm: 'auto' }, textTransform: 'none' }}
              >
                {t('validation_workspace.split_review_group_selected', { count: selectedPages.length })}
              </Button>
            </Box>
          </Tooltip>
        )}
        {selectedPages.length > 0 && (
          <Tooltip title={t('validation_workspace.split_review_remove_selected_tooltip')} arrow>
            <Box component="span" sx={{ display: 'inline-flex', width: { xs: '100%', sm: 'auto' } }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={disabled}
                onClick={() => removePage(selectedPages)}
                startIcon={<DeleteOutlineRoundedIcon />}
                sx={{ width: { xs: '100%', sm: 'auto' }, textTransform: 'none' }}
              >
                {t('validation_workspace.split_review_remove_selected')}
              </Button>
            </Box>
          </Tooltip>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {t('validation_workspace.split_review_drag_hint')}
      </Typography>
      {activeDocumentIndex >= 0 && (
        <Tooltip title={t('validation_workspace.split_review_show_all_documents_tooltip')} arrow>
          <span>
            <Button
              size="small"
              variant="text"
              onClick={clearDocumentFilter}
              sx={{ mt: 0.25, px: 0, minWidth: 0, textTransform: 'none' }}
            >
              {t('validation_workspace.split_review_show_all_documents')}
            </Button>
          </span>
        </Tooltip>
      )}
      <TextField
        select
        size="small"
        label={t('validation_workspace.split_review_page_view_label')}
        value={pageView}
        onChange={(event) => setPageView(event.target.value)}
        sx={{
          mt: 1,
          minWidth: { xs: '100%', sm: 190 },
          '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
        }}
      >
        <MenuItem value="compact">
          {t('validation_workspace.split_review_page_view_compact')}
        </MenuItem>
        <MenuItem value="list">
          {t('validation_workspace.split_review_page_view_list')}
        </MenuItem>
        <MenuItem value="thumbnails">
          {t('validation_workspace.split_review_page_view_thumbnails')}
        </MenuItem>
      </TextField>
      <Box
        ref={thumbnailContainerRef}
        onDragOver={(event) => handlePageGapDrag(event)}
        onDrop={(event) => handlePageGapDrag(event, true)}
        sx={{
          mt: 1,
          p: 0.75,
          display: 'grid',
          gridTemplateColumns: pageViewConfig.containerColumns,
          gap: 0.5,
          maxHeight: { xs: 420, lg: 520 },
          overflowY: 'auto',
          pr: 0.5,
          border: '1px solid',
          borderColor: 'rgba(87, 87, 176, 0.12)',
          borderRadius: 2,
          bgcolor: 'rgba(248, 249, 253, 0.86)',
        }}
      >
        {visiblePageEntries.map((pageEntry) => {
          const { id: pageId, sourcePage: pageNumber, documentIndex } = pageEntry;
          const documentName = documents[documentIndex]?.type || 'document';
          const previewTarget = dragPreviewTargetPage ? pageEntryById.get(dragPreviewTargetPage) : null;
          const sourcePage = pageEntryById.get(dragSourcePageRef.current);
          const previewDocumentIndex = previewTarget && sourcePage && !dragInsertionTarget
            && (draggedPageSet.has(pageId) || previewTarget.id === pageId)
            ? sourcePage.documentIndex
            : documentIndex;
          const previewDocumentName = documents[previewDocumentIndex]?.type || documentName;
          const isDragPreview = previewDocumentName !== documentName;
          const displayedDocumentName = isDragPreview ? previewDocumentName : documentName;
          const documentColorSeed = getDocumentColorSeed(displayedDocumentName, previewDocumentIndex);
          const documentBackgroundColor = generatePastelColor(documentColorSeed);
          const documentAccentColor = generateSaturatedColor(documentColorSeed);
          const thumbnail = thumbnailByPage.get(pageNumber);
          const visionOrientation = visionOrientationByPage[pageNumber] || { rotation: 0, fineAngle: 0 };
          const thumbnailRotation = (pageRotations?.[pageNumber] || 0) - visionOrientation.fineAngle;
          const thumbnailScale = getRotatedPdfThumbnailScale(
            pageViewConfig.thumbnailWidth - 2,
            pageViewConfig.thumbnailHeight - 2,
            thumbnailRotation,
          );
          const isSelected = selectedPageSet.has(pageId);
          const isPageHovered = hoveredPage === pageId;
          const isHoverNavigationLoading = hoverNavigationTarget?.id === pageId;
          const isDragTarget = dragPreviewTargetPage === pageId
            && draggedPages.length > 0
            && !draggedPageSet.has(pageId);
          const isDragInsertionTarget = isDragTarget && dragInsertionTarget?.pageId === pageId;
          const isDragMoveTarget = isDragTarget && !isDragInsertionTarget;
          const insertAfter = isDragInsertionTarget && dragInsertionTarget.insertAfter;
          const isHorizontalInsertion = pageView !== 'list';
          const pageTooltip = isDragInsertionTarget
            ? t(insertAfter
              ? 'validation_workspace.split_review_page_drop_reorder_after'
              : 'validation_workspace.split_review_page_drop_reorder_before')
            : isDragMoveTarget
              ? t('validation_workspace.split_review_page_drop_move', { document: previewDocumentName })
              : t('validation_workspace.split_review_page_tooltip');
          return (
            <Tooltip
              key={pageId}
              title={pageTooltip}
              arrow
            >
              <Paper
                ref={(node) => setThumbnailCardRef(pageId, node)}
                data-page-number={pageNumber}
                data-page-instance-id={pageId}
                data-drag-insertion-position={isDragInsertionTarget ? (insertAfter ? 'after' : 'before') : undefined}
                data-drag-drop-mode={isDragMoveTarget ? 'move' : isDragInsertionTarget ? 'reorder' : undefined}
                component="div"
                role="button"
                tabIndex={0}
                variant="outlined"
                draggable={!disabled}
                onMouseEnter={() => {
                  setHoveredPage(pageId);
                  schedulePageHover(pageEntry);
                }}
                onMouseLeave={() => {
                  cancelPageHover();
                  setHoveredPage((previousPage) => (
                    previousPage === pageId ? null : previousPage
                  ));
                  setHoverNavigationTarget((previousTarget) => (
                    previousTarget?.id === pageId ? null : previousTarget
                  ));
                }}
                onDragStart={(event) => handlePageDragStart(event, pageEntry)}
                onDragOver={(event) => handlePageDragOver(event, pageId)}
                onDragEnd={clearDragPreview}
                onDrop={(event) => handlePageDrop(event, pageId)}
                onClick={(event) => togglePageSelection(pageEntry, event)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    togglePageSelection(pageEntry, event);
                  }
                }}
                aria-pressed={isSelected}
                aria-label={t('validation_workspace.split_review_page_with_document', {
                  page: pageNumber,
                  document: documentName,
                })}
                aria-busy={isHoverNavigationLoading}
                sx={{
                  minWidth: 0,
                   width: 'auto',
                   position: 'relative',
                   overflow: isDragInsertionTarget ? 'visible' : 'hidden',
                  borderRadius: 2,
                  display: 'grid',
                  gridTemplateColumns: pageViewConfig.cardColumns,
                  alignItems: 'center',
                  gap: 0.9,
                  p: 0.7,
                  textAlign: 'left',
                   cursor: disabled ? 'default' : 'pointer',
                   borderColor: isDragMoveTarget
                     ? 'success.main'
                     : isPageHovered || isSelected ? 'primary.main' : 'rgba(87, 87, 176, 0.16)',
                   borderWidth: 1,
                   bgcolor: isDragMoveTarget
                     ? 'rgba(46, 125, 50, 0.1)'
                     : isPageHovered || isSelected ? 'rgba(87, 87, 176, 0.1)' : 'rgba(255, 255, 255, 0.92)',
                   boxShadow: isDragMoveTarget
                     ? '0 0 0 3px rgba(46, 125, 50, 0.14), 0 6px 14px rgba(46, 125, 50, 0.14)'
                     : isPageHovered || isSelected ? '0 6px 14px rgba(87, 87, 176, 0.12)' : '0 2px 6px rgba(35, 38, 82, 0.04)',
                   transform: 'none',
                   transition: 'transform 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                   '&:focus-visible': {
                     outline: '2px solid',
                     outlineColor: 'primary.main',
                     outlineOffset: 2,
                   },
                   '&::after': {
                     content: '""',
                     position: 'absolute',
                     left: 0,
                     right: 0,
                     bottom: 0,
                     height: isHoverNavigationLoading ? 3 : 0,
                     background: 'linear-gradient(90deg, #5757b0, #8c8cda)',
                     transformOrigin: 'left center',
                     transform: isHoverNavigationLoading ? 'scaleX(1)' : 'scaleX(0)',
                     transition: isHoverNavigationLoading
                       ? 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1), height 120ms ease'
                       : 'transform 120ms ease, height 120ms ease',
                   },
                  '&:active': { cursor: disabled ? 'default' : 'grabbing' },
                }}
               >
               <Box
                sx={{
                  width: pageViewConfig.thumbnailWidth,
                  height: pageViewConfig.thumbnailHeight,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                   bgcolor: 'rgba(226, 232, 240, 0.72)',
                   border: '1px solid rgba(148, 163, 184, 0.22)',
                   borderRadius: 1.25,
                  overflow: 'hidden',
                }}
              >
                {thumbnail ? (
                  <Box
                    component="img"
                    src={thumbnail}
                    alt={t('validation_workspace.split_review_page_alt', { page: pageNumber })}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      transform: `rotate(${thumbnailRotation}deg) scale(${thumbnailScale})`,
                      transformOrigin: 'center center',
                    }}
                  />
                ) : thumbnailLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                    {pageNumber}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.45,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontSize: '0.66rem', lineHeight: 1.1 }}
                >
                  {t('validation_workspace.split_review_page_label', { page: pageNumber })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                  <DragIndicatorRoundedIcon
                    aria-hidden="true"
                    sx={{ color: 'text.disabled', fontSize: 16, flexShrink: 0 }}
                  />
                  <Chip
                    size="small"
                    label={displayedDocumentName}
                    title={displayedDocumentName}
                    sx={{
                      height: pageViewConfig.documentChipHeight,
                      maxWidth: 'calc(100% - 28px)',
                      alignSelf: 'flex-start',
                      bgcolor: documentBackgroundColor,
                      color: documentAccentColor,
                      border: `1px solid ${documentAccentColor}`,
                      fontWeight: 700,
                      ...(isDragPreview && {
                        boxShadow: '0 0 0 1px rgba(87, 87, 176, 0.18)',
                      }),
                      '& .MuiChip-label': {
                        px: 0.65,
                        fontSize: pageViewConfig.documentFontSize,
                        lineHeight: 1.1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                  {pageEntry.duplicatedFrom && (
                    <Chip
                      size="small"
                      label={t('validation_workspace.split_review_page_copy_badge')}
                      sx={{ height: pageViewConfig.documentChipHeight, flexShrink: 0, fontSize: '0.62rem' }}
                    />
                  )}
                </Box>
              </Box>
              <IconButton
                size="small"
                disabled={disabled}
                aria-label={t('validation_workspace.split_review_page_actions', { page: pageNumber })}
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  setPageMenu({ anchorEl: event.currentTarget, pageId });
                }}
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  zIndex: 1,
                  p: 0.25,
                  bgcolor: 'rgba(255, 255, 255, 0.82)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.98)' },
                }}
              >
                <MoreVertRoundedIcon fontSize="small" />
              </IconButton>
              {isDragInsertionTarget && (
                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    ...(isHorizontalInsertion
                      ? {
                        top: 4,
                        bottom: 4,
                        width: 14,
                        ...(insertAfter ? { right: -7 } : { left: -7 }),
                      }
                      : {
                        left: 4,
                        right: 4,
                        height: 14,
                        ...(insertAfter ? { bottom: -7 } : { top: -7 }),
                      }),
                    zIndex: 3,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      ...(isHorizontalInsertion
                        ? {
                          top: 0,
                          bottom: 0,
                          left: '50%',
                          width: 3,
                          transform: 'translateX(-50%)',
                        }
                        : {
                          left: 0,
                          right: 0,
                          top: '50%',
                          height: 3,
                          transform: 'translateY(-50%)',
                        }),
                      borderRadius: 999,
                      bgcolor: 'primary.main',
                      boxShadow: '0 2px 6px rgba(87, 87, 176, 0.28)',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: 9,
                      height: 9,
                      transform: 'translate(-50%, -50%)',
                      border: '2px solid',
                      borderColor: 'background.paper',
                      borderRadius: '50%',
                      boxSizing: 'content-box',
                      bgcolor: 'primary.main',
                      boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.16)',
                    }}
                  />
                </Box>
              )}
            </Paper>
            </Tooltip>
          );
        })}
      </Box>

      <Menu
        anchorEl={pageMenu.anchorEl}
        open={Boolean(pageMenu.anchorEl && pageMenuEntry)}
        onClose={() => setPageMenu({ anchorEl: null, pageId: null })}
        MenuListProps={{
          'aria-label': t('validation_workspace.split_review_page_actions_menu'),
          onMouseDown: (event) => event.stopPropagation(),
          onTouchStart: (event) => event.stopPropagation(),
        }}
      >
        {pageMenuEntry && (
          <MenuItem
            onClick={() => duplicatePage(pageMenuEntry.id)}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
          >
            <ListItemIcon><ContentCopyRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('validation_workspace.split_review_duplicate_page')} />
          </MenuItem>
        )}
        {pageMenuEntry && documents
          .filter((document) => document.id !== pageMenuEntry.documentId)
          .map((document) => (
            <MenuItem
              key={document.id}
              onClick={() => movePageToDocument(pageMenuEntry.id, document.id)}
              onMouseDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
            >
              <ListItemIcon><DragIndicatorRoundedIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary={t('validation_workspace.split_review_move_page_to', { document: document.type })}
              />
            </MenuItem>
          ))}
        {pageMenuEntry && (
          <MenuItem
            onClick={() => removePage([pageMenuEntry.id])}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
          >
            <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('validation_workspace.split_review_remove_page')} />
          </MenuItem>
        )}
      </Menu>

      {excludedPages.length > 0 && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            border: '1px dashed',
            borderColor: 'rgba(211, 47, 47, 0.35)',
            borderRadius: 2,
            bgcolor: 'rgba(211, 47, 47, 0.035)',
          }}
        >
          <Typography variant="subtitle2" fontWeight={800} color="error.dark">
            {t('validation_workspace.split_review_excluded_pages', { count: excludedPages.length })}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
            {excludedPages.map((pageNumber) => (
              <Box
                key={pageNumber}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.35,
                  pl: 0.6,
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  border: '1px solid rgba(211, 47, 47, 0.2)',
                }}
              >
                <Typography variant="caption">
                  {t('validation_workspace.split_review_page_label', { page: pageNumber })}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  disabled={disabled}
                  onClick={() => restorePage(pageNumber)}
                  startIcon={<RestoreFromTrashRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{ minWidth: 0, px: 0.6, textTransform: 'none' }}
                >
                  {t('validation_workspace.split_review_restore_page')}
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {thumbnailError && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {thumbnailError}
        </Alert>
      )}

      <Divider sx={{ my: 1.5, borderColor: 'rgba(87, 87, 176, 0.12)' }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 1.5,
            color: 'primary.main',
            bgcolor: 'rgba(87, 87, 176, 0.1)',
          }}
        >
          <ContentCutRoundedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={800}>
          {t('validation_workspace.split_review_summary_title')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {documents.map((document, index) => {
          const documentColorSeed = getDocumentColorSeed(document.type, index);
          const documentBackgroundColor = generatePastelColor(documentColorSeed);
          const documentAccentColor = generateSaturatedColor(documentColorSeed);
          const isActiveDocument = activeDocumentIndex === index;
          return (
            <Box
              key={`${document.pagenbs}-${index}`}
              role="button"
              tabIndex={0}
              aria-pressed={isActiveDocument}
              onClick={() => toggleDocumentFilter(document)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleDocumentFilter(document);
                }
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: 'auto minmax(0, 1fr) 150px' },
                alignItems: 'center',
                gap: 0.5,
                minHeight: 42,
                p: 0.75,
                border: '1px solid',
                borderColor: isActiveDocument ? 'primary.main' : 'rgba(87, 87, 176, 0.12)',
                borderRadius: 1.75,
                cursor: 'pointer',
                bgcolor: isActiveDocument ? 'rgba(87, 87, 176, 0.1)' : 'rgba(255, 255, 255, 0.72)',
                boxShadow: isActiveDocument ? '0 5px 12px rgba(87, 87, 176, 0.1)' : '0 2px 6px rgba(35, 38, 82, 0.03)',
                transition: 'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                '&:hover': {
                  borderColor: isActiveDocument ? 'primary.main' : 'rgba(87, 87, 176, 0.28)',
                  bgcolor: 'rgba(87, 87, 176, 0.06)',
                  boxShadow: '0 5px 12px rgba(35, 38, 82, 0.06)',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 1,
                },
              }}
            >
              <Chip
                size="small"
                label={document.type}
                title={document.type}
                sx={{
                  bgcolor: documentBackgroundColor,
                  color: documentAccentColor,
                  border: `1px solid ${documentAccentColor}`,
                  fontWeight: 700,
                  justifySelf: 'start',
                  maxWidth: '100%',
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  minWidth: 0,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  overflowWrap: 'anywhere',
                }}
              >
                {t('validation_workspace.split_review_pages_list', { pages: formatPageRanges(document.pages) })}
              </Typography>
              <TextField
                size="small"
                variant="standard"
                value={document.type}
                onChange={(event) => updateDocumentType(index, event.target.value)}
                disabled={disabled}
                label={t('validation_workspace.split_review_document_name')}
                fullWidth
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                sx={{
                  minWidth: 0,
                  gridColumn: { xs: '1 / -1', sm: 'auto' },
                  '& .MuiInputBase-root': { fontSize: '0.8rem' },
                }}
              />
            </Box>
          );
        })}
      </Box>

      {documents.length < 2 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('validation_workspace.split_review_min_documents')}
        </Alert>
      )}
      {hasEmptyDocument && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('validation_workspace.split_review_empty_document')}
        </Alert>
      )}
      {unassignedPages.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('validation_workspace.split_review_unassigned_pages', { pages: unassignedPages.join(', ') })}
        </Alert>
      )}

      <Box
        sx={{
          mt: 1.75,
          p: 1,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          border: '1px solid',
          borderColor: 'rgba(87, 87, 176, 0.12)',
          borderRadius: 2,
          bgcolor: 'rgba(248, 249, 253, 0.86)',
        }}
      >
        <Tooltip title={t('validation_workspace.split_review_approve_tooltip')} arrow>
          <Box component="span" sx={{ display: 'inline-flex', flex: { xs: '1 1 100%', sm: '1 1 0' } }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={disabled || saving || !canApprove}
              onClick={onApprove}
              startIcon={<CheckCircleOutlineIcon />}
              sx={{
                width: '100%',
                minHeight: { xs: 46, sm: 44 },
                px: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 800,
                letterSpacing: '0.01em',
                background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
                boxShadow: '0 8px 16px rgba(46, 125, 50, 0.2)',
                transition: 'transform 120ms ease, box-shadow 120ms ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #276a2b 0%, #388e3c 100%)',
                  boxShadow: '0 10px 20px rgba(46, 125, 50, 0.28)',
                  transform: 'translateY(-1px)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.72)',
                  background: 'rgba(46, 125, 50, 0.42)',
                  boxShadow: 'none',
                },
              }}
            >
              {t('validation_workspace.split_review_approve')}
            </Button>
          </Box>
        </Tooltip>
        <Tooltip title={t('validation_workspace.split_review_reject_tooltip')} arrow>
          <Box component="span" sx={{ display: 'inline-flex', flex: { xs: '1 1 100%', sm: '1 1 0' } }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={disabled || saving}
              onClick={onReject}
              startIcon={<CancelOutlinedIcon />}
              sx={{
                width: '100%',
                minHeight: { xs: 46, sm: 44 },
                px: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 800,
                letterSpacing: '0.01em',
                color: 'error.dark',
                borderColor: 'rgba(211, 47, 47, 0.36)',
                bgcolor: 'rgba(211, 47, 47, 0.035)',
                transition: 'transform 120ms ease, border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                '&:hover': {
                  color: 'error.dark',
                  borderColor: 'error.main',
                  bgcolor: 'rgba(211, 47, 47, 0.08)',
                  boxShadow: '0 8px 16px rgba(211, 47, 47, 0.1)',
                  transform: 'translateY(-1px)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.32)',
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  bgcolor: 'transparent',
                },
              }}
            >
              {t('validation_workspace.split_review_reject')}
            </Button>
          </Box>
        </Tooltip>
      </Box>
      <Snackbar
        open={Boolean(actionNotice)}
        autoHideDuration={5000}
        onClose={() => setActionNotice('')}
        message={actionNotice}
        action={undoSnapshot ? (
          <Button
            color="secondary"
            size="small"
            startIcon={<UndoRoundedIcon />}
            onClick={undoLastAction}
            sx={{ textTransform: 'none' }}
          >
            {t('validation_workspace.split_review_undo')}
          </Button>
        ) : undefined}
      />
    </Paper>
  );
};

export default SplitReviewEditor;
